import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { PROJECTS } from "@/data/content";

const easeEditorial = [0.16, 1, 0.3, 1];

function ProjectCard({ project, layoutClass, imageClass, reverse = false }) {
  return (
    <motion.article
      data-testid={`project-card-${project.index}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: easeEditorial }}
      className={`work-card group ${layoutClass}`}
    >
      <motion.div
        initial={{ clipPath: "inset(100% 0 0 0)" }}
        whileInView={{ clipPath: "inset(0% 0 0 0)" }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.2, ease: easeEditorial }}
        className={`relative overflow-hidden border border-black/10 bg-neutral-100 ${imageClass}`}
      >
        <img
          src={project.image}
          alt={project.alt}
          loading="lazy"
          className="work-image w-full h-full object-cover"
        />
        <span
          aria-hidden
          className="absolute top-4 right-4 bg-black text-white text-[10px] uppercase tracking-[0.22em] px-3 py-2 opacity-0 translate-y-1 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0"
        >
          View →
        </span>
      </motion.div>
      <div
        className={`pt-6 md:pt-8 ${
          reverse ? "md:pr-8" : "md:pl-1"
        } flex flex-col`}
      >
        <div className="flex items-baseline justify-between gap-4 mb-4">
          <span
            data-testid={`project-index-${project.index}`}
            className="font-serif text-base text-neutral-500"
          >
            ({project.index})
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">
            {project.year}
          </span>
        </div>
        <p
          data-testid={`project-category-${project.index}`}
          className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-3"
        >
          {project.category}
        </p>
        <h3
          data-testid={`project-title-${project.index}`}
          className="font-serif font-light tracking-tight text-3xl md:text-4xl leading-[1.05] mb-4 max-w-[22ch]"
        >
          {project.title}
        </h3>
        <p
          data-testid={`project-description-${project.index}`}
          className="font-sans text-[15px] md:text-base leading-relaxed text-neutral-700 max-w-[52ch]"
        >
          {project.description}
        </p>
        {project.link && (
          (() => {
            const isExternal = /^https?:\/\//.test(project.link);
            const linkProps = isExternal
              ? { href: project.link, target: "_blank", rel: "noopener noreferrer" }
              : { to: project.link };
            const Tag = isExternal ? "a" : RouterLink;
            return (
              <Tag
                {...linkProps}
                data-testid={`project-link-${project.index}`}
                className="group/link mt-6 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] font-medium self-start"
              >
                <span className="link-underline">
                  {project.linkLabel || "View project"}
                </span>
                <span
                  aria-hidden
                  className="inline-block transition-transform duration-500 group-hover/link:translate-x-1"
                >
                  →
                </span>
              </Tag>
            );
          })()
        )}
      </div>
    </motion.article>
  );
}

export default function Work() {
  return (
    <section
      id="work"
      data-testid="work-section"
      className="px-6 md:px-12 lg:px-16 py-24 md:py-32 lg:py-40"
    >
      {/* Section header */}
      <header className="grid grid-cols-12 gap-6 mb-16 md:mb-28">
        <div className="col-span-6 md:col-span-3">
          <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
            (01) — Selected Work
          </p>
        </div>
        <div className="col-span-12 md:col-span-8 md:col-start-5">
          <h2
            data-testid="work-heading"
            className="font-serif font-light tracking-tight text-4xl sm:text-5xl md:text-6xl leading-[1.05]"
          >
            A selection of frameworks, narratives, and{" "}
            <span className="italic">tones of voice</span> — built for fashion
            houses that speak with intention.
          </h2>
        </div>
      </header>

      {/* Asymmetric grid */}
      <div className="grid grid-cols-12 gap-y-24 md:gap-y-32 gap-x-6 md:gap-x-12">
        {/* Project 01 — large, left */}
        <div className="col-span-12 md:col-span-8">
          <ProjectCard
            project={PROJECTS[0]}
            layoutClass=""
            imageClass="aspect-[4/5] md:aspect-[5/6]"
          />
        </div>

        {/* Project 02 — small, right, offset down */}
        <div className="col-span-12 md:col-span-4 md:col-start-9 md:mt-40">
          <ProjectCard
            project={PROJECTS[1]}
            layoutClass=""
            imageClass="aspect-[3/4]"
            reverse
          />
        </div>

        {/* Project 03 — small, left */}
        <div className="col-span-12 md:col-span-5 md:col-start-1">
          <ProjectCard
            project={PROJECTS[2]}
            layoutClass=""
            imageClass="aspect-[3/4]"
          />
        </div>

        {/* Project 04 — large, right */}
        <div className="col-span-12 md:col-span-7 md:col-start-6 md:mt-24">
          <ProjectCard
            project={PROJECTS[3]}
            layoutClass=""
            imageClass="aspect-[4/5]"
            reverse
          />
        </div>

        {/* Project 05 — small, offset left */}
        <div className="col-span-12 md:col-span-4 md:col-start-2 md:mt-16">
          <ProjectCard
            project={PROJECTS[4]}
            layoutClass=""
            imageClass="aspect-[3/4]"
          />
        </div>
      </div>
    </section>
  );
}
